import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import VotePackageTable from "../components/VotePackages/VotePackageTable";
import VotePackageForm from "../components/VotePackages/VotePackageForm";
import { votePackageService } from "../services/votePackageService";
import { PageHeader, Toolbar, ToolbarField, StatCardRow, ConfirmDialog } from "../components/admin";
import useRowActionFeedback from "../components/admin/useRowActionFeedback";

function VotePackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [deleting, setDeleting] = useState(null);
  const { busyId, flashId, flashKind, run, setFlashId, setFlashKind } = useRowActionFeedback();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const data = await votePackageService.getAllPackages();
      setPackages(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load vote packages");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setEditingPackage(null);
    setFormOpen(true);
  };

  const handleEditClick = (pkg) => {
    setEditingPackage(pkg);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingPackage(null);
  };  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      if (editingPackage) {
        const packageId = formData.packageId;
        delete formData.packageId;
        await votePackageService.updatePackage(packageId, formData);
        toast.success("Vote package updated");
        setFlashId(packageId);
        setFlashKind("success");
      } else {
        await votePackageService.createPackage(formData);
        toast.success("Vote package created");
      }

      handleFormClose();
      await fetchPackages();
      setTimeout(() => setFlashId(null), 1500);
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage = error.response?.data?.message || "Failed to save vote package";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = (pkg) =>
    run({
      id: pkg.packageId,
      action: () => votePackageService.togglePackageStatus(pkg.packageId, !pkg.isActive),
      success: `Package ${pkg.isActive ? "disabled" : "enabled"}`,
      error: "Could not update package status",
      refresh: fetchPackages,
    }).catch(() => {});

  const handleDelete = () =>
    run({
      id: deleting.packageId,
      action: async () => {
        await votePackageService.deletePackage(deleting.packageId);
        setPackages((prev) => prev.filter((p) => p.packageId !== deleting.packageId));
        setDeleting(null);
      },
      success: "Vote package deleted",
      error: "Could not delete vote package",
      flash: "danger",
    }).catch(() => {});

  const activeCount = packages.filter((p) => p.isActive).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vote Packages"
        description="Manage purchasable vote packages"
        actions={
          <button type="button" className="btn-primary" onClick={handleCreateClick}>
            Create package
          </button>
        }
      />

      <StatCardRow
        stats={[
          { label: "Total packages", value: packages.length },
          { label: "Active", value: activeCount },
          { label: "Inactive", value: packages.length - activeCount },
        ]}
      />

      <Toolbar>
        <ToolbarField label="Search by name">
          <input
            type="text"
            placeholder="Search packages…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="field"
          />
        </ToolbarField>
        <ToolbarField label="Sort by" className="max-w-[220px]">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="field">
            <option value="name">Name</option>
            <option value="price">Price (low to high)</option>
            <option value="type">Type</option>
          </select>
        </ToolbarField>
      </Toolbar>

      <VotePackageTable
        packages={packages}
        loading={loading}
        searchTerm={searchTerm}
        sortBy={sortBy}
        busyId={busyId}
        flashId={flashId}
        flashKind={flashKind}
        onEdit={handleEditClick}
        onToggleStatus={handleToggleStatus}
        onDelete={setDeleting}
      />

      <VotePackageForm
        isOpen={formOpen}
        isLoading={submitting}
        initialData={editingPackage}
        onSubmit={handleFormSubmit}
        onClose={handleFormClose}
      />

      {deleting && (
        <ConfirmDialog
          title="Delete vote package"
          message={`Delete "${deleting.name}"? Buyers will no longer see it. This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
          busy={busyId === deleting.packageId}
        />
      )}
    </div>
  );
}

export default VotePackages;
